import React, { useEffect, useState } from "react";
import { Container, Card, Row, Col, Spinner, Alert, Button, Badge } from "react-bootstrap";
import useUserAgreements from "hooks/useUserAgreements"; 
import WidgetSectionTitle from "widgets/WidgetSectionTitle";
import useFormattedDate from "hooks/useFormatDate";
import { Link } from "react-router-dom"; 
import paths from "routes/paths"; 

const ActiveAgreementView = () => {
  const { 
    activeAgreement, 
    getActiveAgreement, 
    loading, 
    error 
  } = useUserAgreements();

  const { formatDateTime } = useFormattedDate();
  const [agreementLoaded, setAgreementLoaded] = useState(false);

  useEffect(() => {
    const fetchActive = async () => {
      try {
        await getActiveAgreement(); 
      } finally {
        setAgreementLoaded(true);
      }
    };
    fetchActive(); 
  }, [getActiveAgreement]);

  // Show spinner until agreement is loaded
  if (!agreementLoaded) {
    return (
      <Container className="text-center py-5">
        <Spinner animation="border" variant="primary" />
        <p className="mt-2 text-primary">Loading Active Agreement...</p>
      </Container>
    );
  }

  // Show error only if loading is done AND error exists
  if (!loading && error) {
    return (
      <Container className="py-5">
        <Alert variant="danger">
          <h4><i className="fas fa-exclamation-triangle me-2"></i>Error Loading Agreement</h4>
          <p>We encountered an error fetching the active agreement: <strong>{error}</strong></p>
        </Alert>
      </Container>
    );
  }

  // Determine the active agreement object
  let agreement = null;
  if (Array.isArray(activeAgreement) && activeAgreement.length > 0) {
    agreement = activeAgreement[0];
  } else if (activeAgreement && activeAgreement.id) {
    agreement = activeAgreement;
  }

  if (!agreement || agreement.status !== "ACTIVE") {
    return (
      <Container className="py-5">
        <Alert variant="warning">
          <h4><i className="fas fa-info-circle me-2"></i>No Active Agreement Found</h4>
          <p>There is currently no agreement with status <strong>ACTIVE</strong>.</p>
          <p>A system administrator must publish a new version from the <Link to={paths.agreements}>User Agreements List</Link>.</p>
        </Alert>
      </Container>
    );
  }

  const agreementTitle = "Active User Agreement";
  const content = agreement.details;

  return (
    <div className="py-4">
      <WidgetSectionTitle
        className="mb-4"
        title={agreementTitle}
        icon="file-contract"
        subtitle="Review the currently active user agreement details."
      />

      <Row>
        <Col lg={12}>
          <Card className="shadow-sm">

            {/* --- Agreement Meta Info --- */}
            <Card.Body className="p-4">
              <Row className="mb-4">
                <Col md={6}>
                  <p><strong>ID:</strong> {agreement.id}</p>
                  <p><strong>Version:</strong> {agreement.version}</p>
                </Col>
                <Col md={6}>
                  <p><strong>Created At:</strong> {formatDateTime(agreement.createdAt)}</p>
                  <p><strong>Updated At:</strong> {formatDateTime(agreement.updatedAt)}</p>
                  <p><strong>Created By:</strong> {agreement.createdBy}</p>
                </Col>
              </Row>

              {/* --- Scrollable Agreement Content --- */}
              <div
                className="border rounded p-3"
                style={{
                  maxHeight: "400px",
                  overflowY: "auto",
                }}
              >
                {content ? (
                  <div dangerouslySetInnerHTML={{ __html: content }} />
                ) : (
                  <div className="text-muted text-center py-5">
                    <i className="fas fa-file-alt fa-3x mb-3"></i>
                    <p className="mb-0">Agreement content (details) is empty.</p>
                  </div>
                )}
              </div>

            </Card.Body>

            <Card.Footer className="text-end">
              <Button variant="outline-primary" as={Link} to={paths.agreements}>
                <i className="fas fa-list me-2"></i>Go back to list
              </Button>
            </Card.Footer>

          </Card>
        </Col>
      </Row>
    </div>
  );
};

export default ActiveAgreementView;
